// @flow

/* global fetch */
import Logger from 'js-logger';
import * as React from 'react';
import styled from 'styled-components';
import includes from 'lodash/includes';

import $ from 'jquery';

import externalPageTarget from '../../../lib/external-page-target';
import notifyCall2ActionReached from '../../../lib/notify-call2action-reached';
import sendJIEvent from '../../../lib/kwizzad-ji';
import { t } from '../../../lib/i18n';
import layout from '../../../style/layout';
import sizes from '../../../style/sizes';

import QuestionContent from './question-content';
import LetterPool from './letterpool';
import QuizPagination from './quiz-pagination';

import RightAnswerIcon from './right-answer-icon';
import WrongAnswerIcon from './wrong-answer-icon';
import HintIcon from './hint-icon';

import type { InternalChallenge, QuizAnswer } from '../../../lib/Challenge';


// A 1x1 transparent pixel GIF.
const emptyImageUrl =
    'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEAAAAALAAAAAABAAEAAAI=';

const preloadedImageUrls = [];




type Props = {
    challenge: InternalChallenge,
    nextChallengeUrl: (() => string),
    nextChallenge: (() => InternalChallenge),
    className: string,
    count: number,
    next: (() => void),
    styleConfiguration: ((string) => any),
    challengeTrackProperties: {
        percent: number,
        challengeCount: number,
        challengeIndex: number,
        type: number,
        sdkType: ?string,
    },
    onChallengeResult: ((isCorrect: boolean) => void),
};


type State = {
    selectedAnswer: ?QuizAnswer | true,
    correct: boolean,
    imageLoaded: boolean,
    hint: boolean,
    poll: boolean,
    solution: string,
    height: number,
    imageSize: { width: number, height: number },
    disableClicks: boolean,
    fadeClass: string,
    showCorrectAnswer: boolean,
    isHintShowed: boolean,
};


class MultipleChoiceQuiz extends React.Component<Props, State> {
    state = {
        selectedAnswer: null,
        correct: false,
        hint: this.props.challenge.statisticsHelper && this.props.challenge.statisticsHelper.hintCount <= 0 ? true : false,
        poll: false,
        imageLoaded: false,
        solution: '',
        height: window.innerHeight,
        imageSize: { width: 300, height: 300 },
        disableClicks: false,
        fadeClass: '',
        showCorrectAnswer: false,
        isHintShowed: false,
    };

    setHeight: ?(() => void);
    correctAnswerTimeout: ?number;

    constructor(props: Props) {
        super(props);

        this.state = Object.assign(this.state, {
            imageSize: this.imageSize(),
        });

        window.quiz = this;
    }

    componentWillMount() {
        this.setHeight = () => {
            const height = window.innerHeight;
            if (height !== 0) {
                // Height might not be set yet on first rendering, so set it again after one frame
                setTimeout(() => {
                    this.setState({ height: window.innerHeight });
                    this.setState({ imageSize: this.imageSize() });
                }, 100);
            }
        };
        window.addEventListener('resize', this.setHeight);
        window.addEventListener('orientationChange', this.setHeight);
    }

    componentDidMount() {
        this.preloadImages(this.props.challenge);
        sendJIEvent('challengeReady', this.props.challenge.num);
        setTimeout(() => this.fadein(), 200);
    }

    componentWillReceiveProps(newProps) {
        if (newProps.challenge !== this.props.challenge) {
            this.preloadImages(newProps.challenge);
        }
    }

    componentDidUpdate() {
        // Logger.debug('quiz update');
        // We could do this, but this would distort numbers because visiting the redirect
        // URL currently has side effects like tracking.
        // this.preloadTargetPageIfPossible();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.setHeight);
        window.removeEventListener('orientationchange', this.setHeight);
        Logger.debug('quiz unmount');
        clearTimeout(this.correctAnswerTimeout);
    }

    preloadImages(challenge: InternalChallenge) {
        if (!challenge) {
            return;
        }
        if (challenge.question) {
            this.preload(challenge.question.imageUrl);
        }
        setTimeout(() => {
            this.preload(challenge.successImageUrl);
            this.preload(challenge.failImageUrl);
            const nextChallenge = this.props.nextChallenge();
            const question = nextChallenge && nextChallenge.question;
            if (question) {
                console.log('Preloading next challenge image');
                this.preload(question.imageUrl);
            }
        }, 1000);
    }

    preloadTargetPageIfPossible() {
        const url = this.props.nextChallengeUrl();
        if (!this.shouldShowTransparentTargetLink || !url) {
            return;
        }

        if (!$('link[data-preload-link="true"]').length) {
            const link = document.createElement('link');
            link.setAttribute('rel', 'prerender preload prefetch');
            link.setAttribute('href', url);
            link.setAttribute('data-preload-link', 'true');
            $('head')[0].appendChild(link);
        }
    }

    preload(imageUrl: ?string) {
        if (!imageUrl || includes(preloadedImageUrls, imageUrl)) {
            return;
        }
        preloadedImageUrls.push(imageUrl);
        const img = new Image();
        img.src = imageUrl;
        img.addEventListener('load', () => console.log(imageUrl, 'preloaded.'));
    }

    closeFeedbackOverlay() {
        console.log('Closing feedback overlay...');
        this.setState({ selectedAnswer: null, showCorrectAnswer: false });
        clearTimeout(this.correctAnswerTimeout);
        if (this.state.correct || (this.props.challenge.punishMode === true & !this.state.hint)) {
            this.fadeout();
        } else {
            if(this.props.challenge.statisticsHelper && this.props.challenge.statisticsHelper.hintCount === 0 && this.state.isHintShowed === false) {
                console.log('disable');
                setTimeout(() => this.setState({ disableClicks: false, hint: true }), 300);
            }else{
                setTimeout(() => this.setState({ disableClicks: false, hint: false }), 300);
            }
        }
    }

    preloadNextChallengeImage() {
        const nextChallenge = this.props.nextChallenge();
        if (nextChallenge && nextChallenge.question) {
            this.preloadImages(nextChallenge);
        }
    }

    answerClick(answer: QuizAnswer) {
        if (this.state.selectedAnswer || this.state.disableClicks) {
            return;
        }

        if (answer.correct) { this.preloadNextChallengeImage(); }

        if (this.props.challenge.punishMode === true) {
            this.props.onChallengeResult(answer.correct);
            if (!answer.correct) {
                this.setState({ showCorrectAnswer: true })
            }
        }
        const timeInterval = 2000 + 10 * (answer.solution || this.props.challenge.successMessage || "").length;
        this.correctAnswerTimeout = setTimeout(() => {
            this.closeFeedbackOverlay();
        }, timeInterval);

        this.setState({
            selectedAnswer: answer,
            poll: answer.poll,
            correct: answer.correct,
            solution: answer.solution,
        });

        this.setState({ disableClicks: true });
        setTimeout(() => this.setState({ disableClicks: false }), 300);

    }

    onLetterpoolSolved(isCorrect: boolean) {
        if (this.props.challenge.punishMode === true) {
            this.props.onChallengeResult(isCorrect);
        }

        this.preloadNextChallengeImage();

        const timeInterval = 3000 + 120 * (this.props.challenge.successMessage || "").length;
        this.correctAnswerTimeout = setTimeout(() => {
            this.closeFeedbackOverlay();
        }, timeInterval);

        const letterpoolChallenge = this.props.challenge.originalLetterpoolChallenge || {};
        const solution = String(isCorrect ? letterpoolChallenge.successMessage : letterpoolChallenge.failMessage);

        this.setState({
            solution,
            selectedAnswer: true,
            correct: isCorrect,
            hint: false,
        });

        this.setState({ disableClicks: true });
        setTimeout(() => this.setState({ disableClicks: false }), 300);
    }

    showHint() {
        const challenge = this.props.challenge.originalLetterpoolChallenge || {};

        if(!isNaN(this.props.challenge.statisticsHelper.hintCount)){
            if(this.state.isHintShowed === false){
                this.props.decreaseHintCount()
            }
        }

        this.setState({
            selectedAnswer: true,
            correct: false,
            hint: true,
            solution: challenge.hintText,
            isHintShowed: true,
        });

    }

    disableClicks(timeInterval = 300) {
        this.setState({ disableClicks: true });
        setTimeout(() => this.setState({ disableClicks: false }), 300);
    }

    handleOverlayClick() {
        if (this.state.selectedAnswer && !this.state.disableClicks) {
            this.closeFeedbackOverlay();
        }
    }

    fadein() {
        if (this.state.fadeClass === 'in') { return; }
        this.setState({ fadeClass: 'in', disableClicks: true });
        setTimeout(() => this.setState({ disableClicks: false }), 300);
    }

    fadeout() {
        if (this.state.fadeClass === 'out') { return; }
        this.setState({ fadeClass: 'out', disableClicks: true });
        setTimeout(() => {
            this.props.next(this.state.correct);
            // Wait a bit to allow images and DOM to be rendered first
            setTimeout(() => this.fadein(), 20);
        }, 500);
    }

    shouldShowTransparentTargetLink() {
        const nextChallenge = this.props.nextChallenge();

        // If the following challenge is a native challenge, let the app take over. In this case, if
        // we'd show a transparent link, clicking it would open it in the current window, which is
        // different from the app opening the link (it might open it externally, in an own Webkit/
        // Chromium view, have specialized handling etc.)
        if (nextChallenge && nextChallenge.isNativeChallenge) return false;

        const isFollowedByWebChallenge = nextChallenge && nextChallenge.type === 'web';
        const isDone = !!this.state.correct && !!this.state.selectedAnswer;
        const hasUrl = !!this.props.nextChallengeUrl();
        return hasUrl && isFollowedByWebChallenge && isDone;
    }

    imageSource() {
        return (this.state.correct ?
            this.props.challenge.successImageUrl :
            this.props.challenge.failImageUrl) || emptyImageUrl;
    }

    padding() {
        return this.props.hasPadding ? (this.isSmallScreen() ? 6 : 15) : 0;
    }

    shorterViewportSize() {
        return Math.min(window.innerWidth, window.innerHeight);
    }

    hasLetterboxFormat() {
        if (!this.state) return false;
        return this.state.height < 320;
    }

    isSmallScreen() {
        return this.shorterViewportSize() <= 412;
    }

    isVerySmallScreen() {
        return this.shorterViewportSize() <= 320;
    }

    fontSize() {
        if (this.hasLetterboxFormat()) {
            return 12.8;
        }
        return this.isVerySmallScreen() ? 15 : 18;
    }

    lineHeight() {
        return 1.2 * this.fontSize();
    }

    // Returns { width, height } for the quiz card image. The image aspect ratio is kept square
    // if possible, but depending on the viewport dimensions it might need to be cropped to have
    // enough space for the content. Here is an example for landscape:
    //
    //    ┌-------┬-┬-----------┐
    //    │       │ │           │
    //    │ image │ │  content  │
    //    │       │ │           │
    //    └-------┴↑┴-----------┘
    // Marked above: The area cropped from the squared image so the content has enough space.
    //
    // The dimensions are calculated according to these constraints:
    //
    // - The image must be as high/wide as the shorter side of the quiz container.
    // - We assume that no matter how the question + answers are oriented, they always
    //   need a specific minimal area that is dependent on the number of buttons and the
    //   line height.
    // - The image should stay in square format if the question + answers is big enough,
    //   otherwise it's cropped at the side that points towards the answer buttons
    //   (cropped width in landscape mode, cropped height in portrait mode)

    imageSize() {
        const isPortrait = window.innerWidth <= window.innerHeight;
        const padding = this.padding();
        const containerSize = {
            width: Math.min(window.innerWidth - (2 * padding), isPortrait ? 500 : 800),
            height: Math.min(window.innerHeight - (2 * padding), isPortrait ? 800 : 400),
        };
        const maximalImageDimension = Math.min(containerSize.width, containerSize.height);
        const containerArea = containerSize.width * containerSize.height;
        const buttonCount = this.props.challenge.answers.length;
        const minimalContentArea = 500 * (buttonCount + 2) * this.lineHeight();
        const maximalImageArea = containerArea - minimalContentArea;
        const imageAreaToCrop = Math.max(0, Math.pow(maximalImageDimension, 2) - maximalImageArea);
        const cropLength = imageAreaToCrop / maximalImageDimension;
        const width = maximalImageDimension - (isPortrait ? 0 : cropLength);
        const height = maximalImageDimension - (isPortrait ? cropLength : 0);

        return {
            width: Math.min(maximalImageDimension, width),
            height: Math.min(maximalImageDimension, height),
        };
    }

    overlayHeadline() {
        if (this.state.hint) return t('hint');
        if (this.state.poll) return t('thanks');
        if (this.state.correct) return t('correct');
        return t('wrong');
    }

    render() {
        const isPortrait = window.innerWidth <= window.innerHeight;
        const challenge = this.props.challenge;
        const isLetterpoolQuiz = challenge.type === 'letterPoolQuiz';
        const { imageSize, selectedAnswer, showCorrectAnswer } = this.state;
        const imageGradient = this.props.styleConfiguration(`imageGradients.${this.state.hint ? 'neutral' : (this.state.correct ? 'right' : 'wrong')}`);
        const solutionState = this.state.correct ? 'solutionIsCorrect' : (this.state.hint ? 'showHint' : 'solutionIsWrong');
        return (
            <div
              className={`challengeContainer ${this.props.className} ${solutionState}`}
              data-correct={!!this.state.correct}
              data-answer-selected={!!this.state.selectedAnswer}
              data-letterbox-format={this.hasLetterboxFormat()}
            >
                <div className="challengeBackgroundContainer">
                    <div className={(() => `quizcontainer ${this.state.fadeClass}`)()}>
                        <div
                          className="quiz"
                          style={{
                              fontSize: this.fontSize(),
                              lineHeight: `${this.lineHeight()}px`,
                              height: isPortrait ? '' : imageSize.height,
                          }}
                        >
                            <div
                              className="imageWrapper"
                              style={{
                                  backgroundImage: challenge.question ? `url(${challenge.question.imageUrl})` : null,
                                  width: `${imageSize.width}px`,
                                  height: `${imageSize.height}px`,
                              }}
                            >
                                <div
                                  className="quizoverlay"
                                  style={{
                                      pointerEvents: this.state.selectedAnswer === null ?
                                        'none' : 'all',
                                      backgroundImage: `url(${this.imageSource()})`,
                                  }}
                                >
                                    <div
                                      className={`overlaySolutionContent ${solutionState}`}
                                      style={{
                                          backgroundColor: imageGradient[0],
                                          background: `linear-gradient(${imageGradient.join(', ')})`,
                                      }}
                                    >
                                        <header>
                                            <RightAnswerIcon />
                                            <WrongAnswerIcon />
                                            <HintIcon />

                                            <div className="overlayHeadline">{this.overlayHeadline()}</div>
                                        </header>

                                        <div className="overlaySolutionText">
                                            {this.state.poll ? this.props.challenge.successMessage : this.state.solution}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            { isLetterpoolQuiz ?
                                <LetterPool
                                    {...{ isPortrait, imageSize }}
                                    style={this.props.style}
                                    challenge={challenge.originalLetterpoolChallenge}
                                    onComplete={() => this.onLetterpoolSolved(true)}
                                    onWrongSolution={() => this.onLetterpoolSolved(false)}
                                    onHintRequest={() => this.showHint()}
                                    showsHint={this.state.hint}
                                    hideToolbar={!!this.state.selectedAnswer}
                                    statisticsHelper={this.props.challenge.statisticsHelper}
                                    decreaseJokerCount={this.props.decreaseJokerCount}
                                /> :
                                <QuestionContent {...{ challenge, isPortrait, imageSize, style: this.props.style, selectedAnswer, showCorrectAnswer, onAnswerClick: (answer: QuizAnswer) => this.answerClick(answer) }}/>
                            }

                        </div>

                        { this.props.paginationCount <= 1 ? null : <QuizPagination
                          // Index begins at 0, so add one
                          index={this.props.paginationIndex}
                          count={this.props.paginationCount}
                        /> }
                    </div>

                    <div
                      className="quizContinueOverlay"
                      onClick={() => this.handleOverlayClick()}
                    />
                </div>

                {/*
                    This is a full-screen transparent link. It's a workaround for the fact that
                    some links have to be opened via a user-event (like a tap) so we should not
                    just redirect there.
                 */}

                {(() => {
                    if (!this.shouldShowTransparentTargetLink()) {
                        return null;
                    }
                    const url = this.props.nextChallengeUrl();
                    const target = externalPageTarget();
                    return (<a // eslint-disable-line jsx-a11y/anchor-has-content
                      className="goalUrlLink"
                      target={target}
                      href={url}
                      onClick={(event) => {
                          event.preventDefault();
                          sendJIEvent('call2Action');
                          sendJIEvent('call2ActionClicked');
                          const callback = () => {
                              notifyCall2ActionReached();
                              window.open(url, target);
                          };
                          setTimeout(callback, 300);
                      }}
                      rel="noreferrer"
                    />);
                })()}
            </div>
        );
    }
}

const noSelectCss = `
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -khtml-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    cursor: default;
`;

const challengeBackgroundContainerCss = `
    .challengeBackgroundContainer {
        padding: ${sizes.bigPadding};
        ${layout.smallScreens(`padding: ${sizes.smallPadding};`)}
    }
`;

const StyledMultipleChoiceQuiz = styled(MultipleChoiceQuiz)`
    ${props => props.hasPadding ? challengeBackgroundContainerCss : ''}

    .quizcontainer {
        width: 100%;
        height: 100%;

        ${layout.portrait(`
            max-width: 500px;
            max-height: 800px;
        `)}

        ${layout.landscape(`
            max-width: 800px;
            max-height: 400px;
        `)}

        position: relative;

        .quizoverlay, .quiz {
            position: absolute;
        }

        .imageWrapper {
            background: white;
            background-position: center center;
            background-size: contain;
            background-repeat: no-repeat;
            position: relative;
            text-align: center;

            /* This lets the element shrink on smaller displays.
            Without this, the image would always be squared. */
            overflow: hidden;

            +portrait() {
                flex: 0 1 auto;
                width: 100%;
                .emptySquareImage {
                    width: 100%;
                }
            }
            +landscape() {
                height: 100%;
                .emptySquareImage {
                    height: 100%;
                }
            }

            .emptySquareImage {
                /* Without this, the image would be aligned on a base line,
                which could cause an unwanted spacing under the image. */
                display: block;
                max-width: 100%;
            }

            .questionImage, .overlayImage {
                position: absolute;
                left: 0;
                top: 0;
                width: 100%;
                height: 100%;

                /* Ensure the image is never stretched. If it's too small, it's cropped instead. */
                object-fit: cover;

                &:not([src]), &[src='']  {
                    display: none;
                }
            }
        }

        .quizoverlay {
            ${noSelectCss}

            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;

            display: flex;
            justify-content: center;
            align-items: center;
            flex-direction: column;

            font-weight: bold;
            text-align: center;

            background-size: cover;

            .overlayHeadline {
                font-weight: 600;
                font-size: 2.25em;
                line-height: 1.5em;
                white-space: nowrap;
            }

            .overlaySolutionContent {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                text-shadow: 0 1px 1px rgba(0, 0, 0, 0.6);

                padding: ${sizes.bigPadding};
                ${layout.verySmallScreens(`padding: ${sizes.smallPadding};`)}
                box-sizing: border-box;

                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                +landscape() {
                    -webkit-box-direction: normal;
                }

                @media (orientation: portrait) and (max-height: 400px) {
                    .overlayHeadline {
                        vertical-align: top;
                        margin-bottom: 0.5em;
                    }
                }

                font-weight: 400;

                /* We need to have both images in DOM
                - without that they would flicker while shown on some devices */

                .feedback-icon {
                    margin: 0.5em;
                    &.wrong {
                        display: inline-block;
                    }
                    &.right {
                        display: none;
                    }
                    &.hint {
                        display: none;
                    }
                }

                &.solutionIsCorrect {
                    .feedback-icon {
                        &.right {
                            display: inline-block;
                        }
                        &.wrong {
                            display: none;
                        }
                        &.hint {
                            display: none;
                        }
                    }
                }

                &.showHint {
                    .feedback-icon {
                        &.right {
                            display: none;
                        }
                        &.wrong {
                            display: none;
                        }
                        &.hint {
                            display: inline-block;
                        }
                    }
                }


                .overlaySolutionText {
                    line-height: 1.5;
                    max-width: 20em;
                    margin: 0 auto;
                    font-weight: bold;
                }

                ${layout.verySmallScreens(`
                    > header {
                        flex-direction: row;
                        display: flex;
                        align-items: center;
                        -webkit-box-direction: normal;

                        .feedback-icon {
                            width: 2em;
                        }
                    }
                    .overlayHeadline {
                        font-size: 1.5em;
                        margin-bottom: 0;
                    }
                    .overlaySolutionText {
                        font-size: 0.9em;
                    }
                `)}
                }
            }
        }

        .quiz {
            width: 100%;
            height: 100%;
            background-color: white;
            text-align: center;

            display: flex;
            ${layout.portrait('flex-direction: column;')}
            ${layout.landscape('flex-direction: row-reverse;')}

            box-shadow: 0 3px 3px rgba(0, 0, 0, 0.25);

            .questionContent {
                display: flex;
                ${layout.portrait('flex: 1 0.5 auto;')}
                ${layout.landscape(`
                    flex: 1;
                    -webkit-box-direction: normal;
                `)}
                box-sizing: border-box;

                justify-content: space-around;
                align-items: stretch;
                flex-direction: column;

                padding: 0 ${sizes.bigPadding};
                ${layout.smallScreens(`padding: ${sizes.bigPadding} ${sizes.smallPadding};`)}

                background-color: white;

                .questionText {
                    color: #222;
                    padding: 0;
                    font-weight: 400;
                    line-height: 1.2;
                    margin-bottom: 0.2em;
                    text-align: center;
                    transform: translateZ(0);
                }
            }

            .answerButtons {
                .answerButton {
                    ${noSelectCss}
                    margin: ${sizes.smallPadding};
                    font-weight: 400;
                    line-height: 1.2;
                    max-lines: 2;
                }

                .answerLayout {
                    background: #0d3349;
                }
            }
        }

        .quizPagination {
            position: absolute;
            overflow: hidden;
            top: 0;
            left: 50%;
            width: 60px;
            height: 30px;
            color: white;
            font-size: 13px;
            line-height: 18px;
            font-weight: 600;
            transform: translate3d(-50%, 0, 0);
            &:before {
                content: '';
                display: block;
                position: absolute;
                left: 50%;
                background-color: ${props => props.styleConfiguration('secondaryColor')};
                width: 40px;
                height: 40px;
                transform: translate(-50%, -50%) rotateZ(45deg);
                +landscape() {
                    background-color: ${props => props.styleConfiguration('secondaryColor')};
                }
            }
            .quizPaginationInner {
                position: absolute;
                left: 0;
                width: 100%;
                text-align: center;
            }
        }
    }

    .quizContinueOverlay, .goalUrlLink {
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        cursor: pointer;
        opacity: 0;
    }

    .answerButton {
        ${layout.smallScreens(`padding: ${sizes.verySmallPadding};`)}

        &.lineBroken {
            width: 50%;
            float: left;
        }

        width: calc(100% - ${sizes.doubleSmallPadding});
        @media (orientation: portrait) {
            width: calc(50% - ${sizes.doubleSmallPadding});
            float: left;
        }
        @media (orientation: landscape) and (min-height: 321px) {
            width: calc(100% - ${sizes.doubleSmallPadding});
        }

        height: 3em;
        @media (orientation: portrait) {
            height: 4em;
        }
    }

    &[data-letterbox-format='true'] {
        .quiz {
            font-size: 0.8em;
        }
        .quizPagination {
            opacity: 0.2;
        }
        .answerButton {
            width: calc(50% - ${sizes.doubleSmallPadding});
            float: left;
            height: 3em;
        }
    }
`;

export default StyledMultipleChoiceQuiz;



// WEBPACK FOOTER //
// ./src/campaign/challenges/quiz/quiz.jsx